import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import GameAPI from '../../frontendServices/gameAPI';
import useGame from '../../hooks/useGame';
import useGameAutoSave from '../../hooks/useGameAutoSaver';
import { useLocale } from '../../hooks/useLocale';
import useRouterMatch from '../../hooks/useRouterMatch';
import PoorInternetStatusProvider from '../../providers/PoorInternetStatusProvider';
import SocketProvider from '../../providers/SocketProvider';
import TimesyncProvider from '../../providers/TimesyncProvider';
import LiveGame from '../_organisms/LiveGame';
import Loader from '../Loader';
import { CenteredContainer } from '../styled/CenteredContainer';

const LiveGamePage = () => {
  useLocale();
  const { update } = useIntercom();
  const { gameId, router, tenantId } = useRouterMatch();
  const session = useSession();
  const { game } = useGame(gameId);
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined | null>(undefined);
  const { autoSave } = useGameAutoSave();
  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      const gameAPI = new GameAPI(
        session.data?.idToken as string,
        session.data?.provider,
      );
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);
  useEffect(() => {
    /* this effect is a catch for entering a game without having a currentRound set
     since games can be created with currentRound == null, but should not be able to be played
     this effect makes sure the user does not end up in this state by setting first round to current or 
     Routing back
    */
    if (game && !game.currentRound) {
      if (game.slides && game.slides.length > 0) {
        autoSave({
          gameId: game.id,
          currentRound: game.slides[0].id,
          slides: game.slides,
        });
      } else {
        router.back();
      }
    }
  }, [autoSave, game, router]);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
    return () => {
      update({
        hideDefaultLauncher: false,
      });
    };
  }, [update]);

  useEffect(() => {
    return () => {
      if (gameAPI) {
        gameAPI.disconnect();
      }
    };
  }, [gameAPI]);

  /** Redirect user to results if they are not authenticated */
  useEffect(() => {
    if (session.status === 'unauthenticated') {
      router.replace(`${router.asPath}/results`);
    }
  }, [session, router]);

  /** Redirect user to results  if they dont have access to the game */
  const userId = session.data?.user.uid;
  const hostUId = game?.host.uid;
  useEffect(() => {
    if (userId && hostUId) {
      if (userId !== hostUId) router.replace(`${router.asPath}/results`);
    }
  }, [hostUId, router, userId]);

  if (session.status === 'loading') {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }
  if (!userId) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  if (!gameAPI) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <SocketProvider gameAPI={gameAPI}>
      <PoorInternetStatusProvider
        tenantId={tenantId}
        uid={userId}
        gameAPI={gameAPI}
        gameId={gameId}
      >
        <TimesyncProvider gameAPI={gameAPI} gameId={gameId}>
          <LiveGame />
        </TimesyncProvider>
      </PoorInternetStatusProvider>
    </SocketProvider>
  );
};

export default LiveGamePage;
